/**
 * Turns an axios error from the user-management API into something a manager can act on.
 *
 * The backend answers with `{ status, message }` for the cases it recognises (duplicate email,
 * missing user, disabled account, ...). When it doesn't, fall back to a status-specific hint
 * instead of a bare "Failed ..." so the person knows whether to retry, ask an admin, or give up.
 */
export const USER_EXISTS_STATUS = 409;

export const describeUserApiError = (error, fallback) => {
  const status = error?.response?.status ?? null;
  const serverMessage = error?.response?.data?.message;
  if (typeof serverMessage === "string" && serverMessage.trim()) {
    return serverMessage.trim();
  }
  if (status === USER_EXISTS_STATUS) return "A user with this email already exists.";
  if (status === 403) return "You don't have permission to do this. Ask an admin.";
  if (status === 401) return "Your session has expired. Please sign in again.";
  if (status === 404) return "This user no longer exists. Refresh the page.";
  if (status === null && error?.message === "Network Error") {
    return `${fallback} Check your connection and try again.`;
  }
  return fallback;
};

export const toSerializableError = (error, message) => ({
  message: message || error?.message || "Network Error",
  status: error?.response?.status ?? null,
  data: error?.response?.data ?? null,
});

/**
 * The user list is read straight from Firestore, so its failures carry a Firestore `code`
 * ("permission-denied", "unavailable", ...) rather than an HTTP status.
 */
export const describeFirestoreError = (error, fallback) => {
  const code = String(error?.code || "").toLowerCase();
  if (code.endsWith("permission-denied")) {
    return "You don't have permission to view the user list. Ask an admin.";
  }
  if (code.endsWith("unauthenticated")) {
    return "Your session has expired. Please sign in again.";
  }
  if (code.endsWith("unavailable") || code.endsWith("deadline-exceeded")) {
    return `${fallback} The user directory is not reachable right now. Check your connection and try again.`;
  }
  if (code.endsWith("resource-exhausted")) {
    return `${fallback} The user directory quota is exhausted. Try again later.`;
  }
  return fallback;
};
